import { ILocale, ILocalizable } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { IValidationIssue } from "infrastructure/validation";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";
import { IIndustry, ITenant, IApiError, IEnterprise } from "ui/shared/models/common";

import { ITokenItem } from "../shared/types/Token.types";

export interface ITranslationMessagesManagerState {
  filter: ITranslationMessagesManagerFilter;
  grid: ITranslationMessagesManagerGrid;
  notifications: INotification[];
  isLoading: boolean;
  isExportDisabled: boolean;
}

export interface ITranslationMessagesManagerFilter {
  locales: ILocale[];
  supportedLocales?: ILocale[];
  industries: IIndustry[];
  tenants: ITenant[];
  enterprises: IEnterprise[];
  text?: string;
  messageId?: string;
  translatedBy: ILocalizable[];
  parents: ILocalizable[];
  isCheckedShowPreviewText: boolean;
  lastData?: ITranslationMessagesManagerFilterData;
}

export interface ITranslationMessagesManagerFilterData {
  selectedLocale?: ILocale;
  selectedIndustry?: IIndustry;
  selectedEnterprise?: IEnterprise;
  selectedTenant?: ITenant;
  filterText?: string;
  filterMessageId?: string;
  selectedTranslatedBy?: ILocalizable;
  selectedParent?: ILocalizable;
  isCheckedShowPreviewText?: boolean;
}

export interface ITenantsPickerSearchCriterion extends IPickerSearchCriterion<ITenant> {
  enterpriseId: string;
}

export interface ITranslationMessageRow {
  data: ITranslationMessageItem;
  originalData?: ITranslationMessageItem;
  isTranslationChanged: boolean;
  index: number;
  validationIssues: IValidationIssue[];
}

export interface ITranslationMessageItem {
  locale: string;
  messageId: string;
  sourceText: string;
  industry?: string;
  translatedText?: string;
  tokens: ITokenItem[];
  textPreview: string;
  translationStatus: string;
  version: number;
  isParentMessage?: boolean;
}

export interface ITranslationMessagesManagerGrid {
  gridData: ITranslationMessageRow[];
  selected: ITranslationMessageRow[];
  isSearched: boolean;
}

export interface IChangeText {
  index: number;
  text?: string;
}

export interface ITranslationMessageModifyData {
  isTranslationChanged: boolean;
  index: number;
  translatedText?: string;
  originalData?: ITranslationMessageItem;
  messageId: string;
  validationIssues: IValidationIssue[];
}

export interface ITranslationErrorModel {
  messageId: string;
  error: IApiError;
}

export const getInitialTranslationMessagesManagerState = (): ITranslationMessagesManagerState => ({
  filter: {
    locales: [],
    supportedLocales: [],
    industries: [],
    enterprises: [],
    tenants: [],
    text: "",
    messageId: "",
    translatedBy: [],
    parents: [],
    isCheckedShowPreviewText: false
  },
  grid: { gridData: [], selected: [], isSearched: false },
  isExportDisabled: true,
  notifications: [],
  isLoading: false
});

export type SelectorState = { translationMessagesManager: ITranslationMessagesManagerState };

const getStateSelectorsProvider = () => ({
  getFilter: (state: SelectorState) => state.translationMessagesManager.filter,
  getFilterLastData: (state: SelectorState) => state.translationMessagesManager.filter.lastData,
  getGridRows: (state: SelectorState) => state.translationMessagesManager.grid.gridData,
  getNotifications: (state: SelectorState) => state.translationMessagesManager.notifications
});

export const selectors = getStateSelectorsProvider();
